import type { Report } from "../../lib/api/index.js";
import { useCaptureProgress } from "../../lib/queries/index.js";
import { Check, Pause } from "../icons.js";
import { Bloom } from "../loader/index.js";

/** A quiet report is asked once a minute whether a batch has started. */
const IDLE_POLL_MS = 60_000;

/**
 * The end of a brand's row in the brand menu: what it is doing (DESIGN.md §5).
 *
 * Its captures running beat everything — that is the one thing on the row that
 * moves — then a pause, then the tick on the brand on screen. A first read
 * still in flight, or one that failed, shows what the report alone can say:
 * the menu has no room to explain a failed read, and the brand's own screens
 * do.
 */
export function ReportState({
  workspaceId,
  report,
  isCurrent,
}: {
  readonly workspaceId: string;
  readonly report: Report;
  readonly isCurrent: boolean;
}) {
  const progress = useCaptureProgress(workspaceId, report.id, {
    idlePollMs: IDLE_POLL_MS,
  }).data;

  if (progress !== undefined && progress.pending > 0) {
    const finished =
      progress.answered + progress.unanswered.reduce((sum, row) => sum + row.count, 0);
    const counted = `${String(finished)} of ${String(progress.planned)} captures in`;
    // Held for the budget, nothing moves: the mark would say "working" when it is not.
    const held = progress.heldFor === "budget";
    const line = held
      ? `Waiting: this month's capture budget is used up · ${counted}`
      : `Asking ${String(progress.pending)} more ${progress.pending === 1 ? "question" : "questions"} · ${counted}`;
    return (
      <>
        <span
          className={held ? "side-capture side-capture-held" : "side-capture"}
          title={line}
          aria-hidden="true"
        >
          {held ? <Pause size={11} /> : <Bloom size={11} motion="assemble" />}
          <span className="side-capture-count">
            <b>{finished}</b>/{progress.planned}
          </span>
        </span>
        <span className="visually-hidden">
          , {held ? "captures waiting" : "capturing"}: {line}
        </span>
      </>
    );
  }

  if (report.paused) {
    return (
      <>
        <Pause className="side-menu-paused" aria-hidden="true" />
        <span className="visually-hidden"> (paused)</span>
      </>
    );
  }

  return isCurrent ? <Check className="side-menu-check" aria-hidden="true" /> : null;
}
